import { Box, Heading, Image, useToast } from "@chakra-ui/react";
import { useState } from "react";
import PoweredByStravaLogo from "../../assets/api_logo_pwrdBy_strava_stack_gray.svg";
import { useExternalServices } from "../../contexts/externalServicesContext";
import type ConnectedService from "../../domain/ConnectedService";
import { cardHeading, glassCard } from "../../themes/design";
import ConnectButton from "./ConnectButton";
import SynchronizeButton from "./SynchronizeButton";

interface ConnectedServiceStatusProps {
  connectedService: ConnectedService;
}

const ConnectedServiceStatus: React.FC<ConnectedServiceStatusProps> = ({ connectedService }) => {
  const { strava } = useExternalServices();
  const toast = useToast();

  const [syncStatus, setSyncStatus] = useState(connectedService.synchronizationStatus());

  const resync = async (continueFromHighWaterMark: boolean): Promise<void> => {
    console.log(`Refreshing data from ${connectedService.serviceName}`);
    if (connectedService.serviceName === "Strava") {
      const ok = await strava.startSyncAsync(continueFromHighWaterMark);
      if (!ok) {
        console.log("Strava sync failed");
        toast({
          title: "Strava sync failed.",
          description: strava.error?.message ?? "Strava sync failed.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setSyncStatus(connectedService.synchronizationStatus());
      } else {
        console.log("Strava sync successfully started");
        setSyncStatus("IN_PROGRESS");
      }
    }
  };

  const stopSync = async (): Promise<void> => {
    console.log(`Stopping synchronization with ${connectedService.serviceName}`);
    if (connectedService.serviceName === "Strava") {
      await strava.stopSyncAsync();
      console.log("Strava sync successfully stopped");
      setSyncStatus("ABORTED");
    }
  };

  const connect = (): void => {
    console.log(`Connecting to ${connectedService.serviceName}`);
    if (connectedService.serviceName === "Strava") {
      strava.startConnect();
    }
  };

  return (
    <Box
      {...glassCard}
      color="primary.100"
      minH="200px"
      minW="400px"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      <Heading {...cardHeading}>
        {connectedService.serviceName === "Strava" ? (
          <Image src={PoweredByStravaLogo} alt="Strava" h="50px" w="auto" />
        ) : (
          connectedService.formattedName
        )}
      </Heading>

      {connectedService.isConnected() ? (
        <SynchronizeButton syncStatus={syncStatus} resync={resync} stopSync={stopSync} />
      ) : (
        <ConnectButton connectedService={connectedService} connect={connect} />
      )}
      {strava.error ? <Box mt={3}>{strava.error.message}</Box> : null}
    </Box>
  );
};

export default ConnectedServiceStatus;
